import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Clock, Loader2, Pause, Play, Square, HelpCircle, Coffee, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiGet } from '@/lib/api';

const CLOCK_POLL_MS = 20000;

/**
 * Phase 08 — the session runner.
 * The clock on screen is whatever the SERVER says it is; this component never
 * counts time itself. Pause and End are always one tap away, and asking a
 * question is never treated as being off-task.
 */
export function SessionRunnerDialog({ open, onOpenChange, seed }) {
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const startedRef = useRef(false);

  const refresh = useCallback(async (id) => {
    try { setSession(await apiGet(`/premium/v1/runner/sessions/${id}`)); } catch { /* keep last state */ }
  }, []);

  useEffect(() => {
    if (!open || startedRef.current || !seed?.chapter) return;
    startedRef.current = true;
    (async () => {
      setBusy(true);
      try {
        const res = await apiPost('/premium/v1/runner/sessions', {
          mode: 'ai_tutor', subject: seed.subject, chapter: seed.chapter,
          concept_id: seed.concept_id, concept_title: seed.concept_title,
        });
        setSession(res);
      } catch (e) { toast.error(e?.message || 'Could not open the session'); onOpenChange(false); }
      setBusy(false);
    })();
  }, [open, seed, onOpenChange]);

  useEffect(() => {
    if (!open || !session?.session_id) return;
    const t = setInterval(() => refresh(session.session_id), CLOCK_POLL_MS);
    return () => clearInterval(t);
  }, [open, session?.session_id, refresh]);

  useEffect(() => { if (!open) { startedRef.current = false; setSession(null); setAnswer(null); } }, [open]);

  const act = async (path, body = {}) => {
    setBusy(true);
    try { setSession(await apiPost(`/premium/v1/runner/sessions/${session.session_id}${path}`, body)); }
    catch (e) { toast.error(e?.message || 'That didn\'t go through'); }
    setBusy(false);
  };

  const advance = async (extra = {}) => {
    setBusy(true);
    try {
      const res = await apiPost(`/premium/v1/runner/sessions/${session.session_id}/advance`, extra);
      setSession(res);
      if (res.content_unavailable) toast.message('The tutor voice is unavailable right now — the session carries on.');
    } catch (e) { toast.error(e?.message || 'Could not move the session on'); }
    setBusy(false);
  };

  const ask = async () => {
    if (!question.trim()) return;
    setBusy(true);
    try {
      const res = await apiPost(`/premium/v1/runner/sessions/${session.session_id}/ask`, { question });
      setAnswer(res.answer); setSession(res.session); setQuestion('');
    } catch (e) { toast.error(e?.message || 'Could not answer that just now'); }
    setBusy(false);
  };

  const end = async () => {
    setBusy(true);
    try {
      const res = await apiPost(`/premium/v1/runner/sessions/${session.session_id}/end`, {});
      toast.success(res.summary?.message || 'Session closed');
      onOpenChange(false);
    } catch (e) { toast.error(e?.message || 'Could not end the session'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="session-runner-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-500" />
            {seed?.concept_title || seed?.chapter || 'Study session'}
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            Exam Mode · {seed?.subject} · {seed?.chapter}
          </DialogDescription>
        </DialogHeader>

        {!session && <p className="font-body text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening your session…</p>}

        {session && (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {/* server clock + rail */}
            <div className="rounded-2xl border border-slate-200 p-3 bg-slate-50/70">
              <div className="flex items-center justify-between">
                <p className="font-heading text-sm text-slate-800 flex items-center gap-1.5" data-testid="runner-clock">
                  <Clock className="w-3.5 h-3.5 text-indigo-500" />
                  {session.active_minutes} / {session.planned_minutes} min
                  <span className="text-[11px] font-body text-slate-400">(server clock)</span>
                </p>
                <div className="flex items-center gap-1.5">
                  {session.paused
                    ? <Button size="sm" variant="outline" className="rounded-xl h-7" onClick={() => act('/resume')} data-testid="runner-resume-btn"><Play className="w-3 h-3 mr-1" /> Resume</Button>
                    : <Button size="sm" variant="outline" className="rounded-xl h-7" onClick={() => act('/pause')} data-testid="runner-pause-btn"><Pause className="w-3 h-3 mr-1" /> Pause</Button>}
                  <Button size="sm" variant="outline" className="rounded-xl h-7 text-rose-600" onClick={end} data-testid="runner-end-btn"><Square className="w-3 h-3 mr-1" /> End</Button>
                </div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all" style={{ width: `${session.progress_pct}%` }} />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="runner-rail">
                {session.rail.map(s => (
                  <span key={s.key} data-testid={`runner-rail-${s.key}`}
                    className={`text-[10px] font-heading px-2 py-0.5 rounded-full border ${s.status === 'current' ? 'bg-indigo-600 text-white border-indigo-600' : s.status === 'done' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-400 border-slate-200'}`}>
                    {s.label}
                  </span>
                ))}
              </div>
            </div>

            {session.cap?.message && (
              <p className="text-[12px] font-body text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2.5" data-testid="runner-cap-message">
                {session.cap.message}
              </p>
            )}

            {session.checkpoint_due && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-2.5 flex items-center justify-between gap-2" data-testid="runner-checkpoint">
                <p className="font-body text-[12px] text-indigo-900">Quick checkpoint — still with me on this bit?</p>
                <Button size="sm" className="rounded-xl h-7" onClick={() => act('/checkpoint', { answered: true })} data-testid="runner-checkpoint-btn">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Yes, carry on
                </Button>
              </div>
            )}

            {session.break_due && (
              <p className="text-[12px] font-body text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 flex items-center gap-1.5" data-testid="runner-break">
                <Coffee className="w-3.5 h-3.5" /> Good point for a five-minute break — tap Pause, walk about, come back.
              </p>
            )}

            <div className="rounded-2xl border border-slate-200 p-3" data-testid="runner-stage">
              <p className="text-[11px] font-heading uppercase tracking-widest text-slate-400">{session.stage_label}</p>
              {session.stage === 'goal' && (
                <p className="font-body text-sm text-slate-700 mt-1" data-testid="runner-goal">
                  Today's goal: understand {seed?.concept_title || seed?.chapter} well enough to explain it to someone else.
                </p>
              )}
              {session.stage_content && (
                <p className="font-body text-sm text-slate-700 mt-1 whitespace-pre-wrap" data-testid="runner-content">{session.stage_content}</p>
              )}
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Button size="sm" className="rounded-xl" disabled={busy || session.stage === 'complete'} onClick={() => advance()} data-testid="runner-next-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} {session.next_hint || 'Next'}
                </Button>
                <Button size="sm" variant="outline" className="rounded-xl" disabled={busy}
                  onClick={() => advance({ confused: true })} data-testid="runner-confused-btn">
                  I didn't follow that
                </Button>
                {session.stage === 'check_mastery' && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-xl" disabled={busy}
                      onClick={() => advance({ mastery_ok: true })} data-testid="runner-mastery-ok-btn">
                      I cleared the mastery quiz
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-xl" disabled={busy}
                      onClick={() => advance({ mastery_ok: false })} data-testid="runner-mastery-miss-btn">
                      I missed the gate
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* clarification at any point */}
            <div className="rounded-2xl border border-slate-200 p-3" data-testid="runner-ask">
              <p className="text-[11px] font-heading uppercase tracking-widest text-slate-400 flex items-center gap-1">
                <HelpCircle className="w-3 h-3" /> Ask me anything, any time
              </p>
              <div className="flex gap-2 mt-1.5">
                <Input value={question} onChange={e => setQuestion(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') ask(); }}
                  placeholder="What does the origin actually mean?"
                  className="rounded-xl text-sm" data-testid="runner-ask-input" />
                <Button size="sm" className="rounded-xl" onClick={ask} disabled={busy} data-testid="runner-ask-btn">Ask</Button>
              </div>
              {answer && <p className="font-body text-[13px] text-slate-700 mt-2 whitespace-pre-wrap" data-testid="runner-ask-answer">{answer}</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SessionRunnerDialog;
